import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import type { AccountErasureRequest } from "@pushback-and-chaos/contracts";
import type { ErasureConfirmResult, ErasureRequestedFacts } from "../data/account-erasure.js";
import { ERASURE } from "../design/copy.js";
import { CaseKindRow } from "./case-card.js";
import { DRAG_Z } from "./case-lightbox.js";
import { Pill } from "./primitives.js";

/**
 * SELF-SERVE ACCOUNT ERASURE, the learner's side (docs/ACCOUNT-ERASURE-DESIGN-2026-10-20.md,
 * "What the learner experiences"): the dialog settings' "delete my account." opens, the notice
 * after confirming, and the full-screen notice a pending account sees instead of the app. None
 * of them talks to the server: every door is a callback, and each one that can fail says so.
 */

/**
 * "November 19, 2026, at 5:30 PM": the instant the deletion runs after, date and time both on
 * the learner's own clock. The purge runs hourly once that instant has passed, and the
 * confirmation email states the same instant in UTC, so a date alone could name a different day
 * from the email's. `timeZone` is for a test; the screens leave it to the browser. Whatever space
 * a runtime puts before AM or PM (some put a narrow no-break one) is written as a plain space.
 */
export function erasureMoment(iso: string, timeZone?: string): string {
  const at = new Date(iso);
  const zone = timeZone === undefined ? {} : { timeZone };
  const date = at.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", ...zone });
  const time = at.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", ...zone });
  return `${date}, at ${time}`.replace(/\s/gu, " ");
}

const BACKDROP = "color-mix(in srgb, var(--pc-ink) 28%, transparent)";
const SAFE_BUTTON =
  "inline-flex cursor-pointer items-center rounded-pill border border-ink-soft bg-transparent px-6 py-2 font-sans text-nav text-ink hover:bg-marker disabled:cursor-not-allowed disabled:opacity-60";

/**
 * aria-modal promises the page behind is out of reach; Tab has to agree. The
 * paper's own enabled controls, first to last, wrap round.
 */
function trapTab(event: KeyboardEvent<HTMLElement>, paper: HTMLElement | null): void {
  if (event.key !== "Tab" || paper === null) return;
  const controls = Array.from(paper.querySelectorAll<HTMLElement>("button:not([disabled]), input:not([disabled])"));
  const first = controls[0];
  const last = controls.at(-1);
  if (first === undefined || last === undefined) {
    event.preventDefault();
    return;
  }
  const active = document.activeElement;
  if (event.shiftKey && (active === first || active === paper)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}

/** A control the learner cannot reach: gone from the page, outside the paper, or disabled under their focus. */
function outOfReach(active: Element | null, paper: HTMLElement): boolean {
  if (active === null || !paper.contains(active)) return true;
  return (active instanceof HTMLButtonElement || active instanceof HTMLInputElement) && active.disabled;
}

/**
 * Focus through a wait. A press disables the doors under the learner's focus,
 * and a browser drops focus to the body, from where Tab leaves an aria-modal
 * paper. While `waiting`, focus that has lost its control rests on the paper
 * (Tab stays trapped there); when the wait ends, focus on the paper or lost
 * goes back to the pressed door if it can be pressed again, else to the safe
 * one. Focus the learner kept on a control is left alone.
 */
function useFocusThroughWait(
  waiting: boolean,
  paperRef: RefObject<HTMLElement | null>,
  pressedRef: RefObject<RefObject<HTMLButtonElement | null> | null>,
  safeRef: RefObject<HTMLButtonElement | null>,
): void {
  const wasWaiting = useRef(waiting);
  useLayoutEffect(() => {
    const before = wasWaiting.current;
    wasWaiting.current = waiting;
    const paper = paperRef.current;
    if (paper === null || waiting === before) return;
    const active = document.activeElement;
    if (waiting) {
      if (outOfReach(active, paper)) paper.focus();
      return;
    }
    if (active !== paper && !outOfReach(active, paper)) return;
    const pressed = pressedRef.current?.current ?? null;
    const target = pressed !== null && pressed.isConnected && !pressed.disabled ? pressed : safeRef.current;
    target?.focus();
  }, [waiting, paperRef, pressedRef, safeRef]);
}

/** Escape anywhere on the page, while the paper is up, is the paper's. */
function useEscape(onEscape: () => void): void {
  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent): void => {
      if (event.key === "Escape") onEscape();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [onEscape]);
}

export interface AccountErasureDialogProps {
  /**
   * GET /me/erasure's word on the address. A verified one is offered the two
   * emailed copies; an unverified one (ruling 17) the two downloads and why;
   * null (not read yet, or the read failed) the two downloads with a line that
   * says nothing about the address. The downloads work for every address.
   */
  readonly emailVerified: boolean | null;
  /** The settings sheet's busy: a download or another save is pending. */
  readonly busy: boolean;
  /**
   * POST /me/erasure with the choices. "requested" once the request is made
   * (the app then logs this browser out and shows the notice); "failed" when
   * it was not and nothing changed; "unsure" when its answer was lost and
   * nothing could say which. The dialog says the last two and stays.
   */
  readonly onConfirm: (choices: AccountErasureRequest) => Promise<ErasureConfirmResult>;
  readonly onCancel: () => void;
  /**
   * The two downloads, each answering whether the copy was saved. The app's
   * own error notice is behind the sheet and this paper, so a failure is said
   * here.
   */
  readonly onDownloadCoaching: () => Promise<boolean>;
  readonly onDownloadAccount: () => Promise<boolean>;
}

type DialogPhase = "asking" | "working" | "failed" | "unsure" | "requested";

/**
 * "Delete your account?" A real modal, built from the start-over question and
 * the guest paper: an alertdialog portalled to the body (the settings sheet is
 * its own fixed layer), focus on the safe button, Tab kept inside, and Escape,
 * the backdrop and "keep my account." all close it with nothing sent. Only
 * "delete my account." sends, and while it is out nothing else can be pressed.
 * Its failure is its own: the dialog stays, says nothing changed, and offers
 * the same door again, with focus back on it.
 */
export function AccountErasureDialog({ emailVerified, busy, onConfirm, onCancel, onDownloadCoaching, onDownloadAccount }: AccountErasureDialogProps): ReactNode {
  const paperRef = useRef<HTMLElement | null>(null);
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const confirmRef = useRef<HTMLButtonElement | null>(null);
  const coachingRef = useRef<HTMLButtonElement | null>(null);
  const accountRef = useRef<HTMLButtonElement | null>(null);
  /** The door last pressed, which focus goes back to after the wait it started. */
  const pressedRef = useRef<RefObject<HTMLButtonElement | null> | null>(null);
  const [copyCoaching, setCopyCoaching] = useState(false);
  const [copyCasebooks, setCopyCasebooks] = useState(false);
  const [phase, setPhase] = useState<DialogPhase>("asking");
  const [downloadFailed, setDownloadFailed] = useState(false);
  const held = phase === "working" || phase === "requested";
  const verified = emailVerified === true;

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);
  // Once the request is made the app closes the sheet and this paper with it: no wait to end.
  useFocusThroughWait(phase !== "requested" && (phase === "working" || busy), paperRef, pressedRef, cancelRef);

  const cancel = (): void => {
    if (!held) onCancel();
  };
  useEscape(cancel);

  const download = (door: () => Promise<boolean>, pressed: RefObject<HTMLButtonElement | null>): void => {
    pressedRef.current = pressed;
    setDownloadFailed(false);
    void door().then(
      (saved) => {
        setDownloadFailed(!saved);
      },
      () => {
        setDownloadFailed(true);
      },
    );
  };

  const confirm = (): void => {
    pressedRef.current = confirmRef;
    setDownloadFailed(false);
    setPhase("working");
    // An unverified address keeps no copy choices: the server records none for it either.
    const choices: AccountErasureRequest = verified ? { copyCoaching, copyCasebooks } : { copyCoaching: false, copyCasebooks: false };
    void onConfirm(choices).then(
      (result) => {
        setPhase(result);
      },
      () => {
        setPhase("unsure");
      },
    );
  };

  return createPortal(
    <div
      data-testid="account-erasure-backdrop"
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: DRAG_Z + 3, background: BACKDROP }}
      onClick={(event) => {
        if (event.target === event.currentTarget) cancel();
      }}
    >
      <section
        ref={paperRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="pc-erasure-title"
        aria-describedby="pc-erasure-body"
        aria-busy={phase === "working"}
        tabIndex={-1}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.stopPropagation();
            cancel();
            return;
          }
          trapTab(event, paperRef.current);
        }}
        className="pc-case-lightbox pc-lightbox-in max-h-[calc(100svh-2rem)] w-full max-w-[40rem] overflow-y-auto outline-none"
      >
        <div className="pc-case-lightbox-bar" style={{ cursor: "default" }}>
          <div className="pc-case-lightbox-kind">
            <CaseKindRow words={ERASURE.kind} glyph="plan" />
          </div>
        </div>
        <div className="pc-case-lightbox-body">
          <h2 id="pc-erasure-title" className="pc-case-lightbox-title">
            {ERASURE.title}
          </h2>
          <div id="pc-erasure-body">
            {ERASURE.explanation.map((line) => (
              <p key={line} className="pc-guest-lightbox-text">
                {line}
              </p>
            ))}
          </div>

          {verified ? (
            <fieldset data-testid="erasure-copies" className="m-0 mt-6 border-0 p-0">
              <legend className="pc-case-lightbox-question p-0">{ERASURE.copiesByEmail}</legend>
              <label className="mt-3 flex cursor-pointer items-start gap-3 font-sans text-body">
                <input type="checkbox" checked={copyCoaching} disabled={held} onChange={(e) => setCopyCoaching(e.target.checked)} className="mt-1" />
                <span>{ERASURE.copyCoaching}</span>
              </label>
              <label className="mt-2 flex cursor-pointer items-start gap-3 font-sans text-body">
                <input type="checkbox" checked={copyCasebooks} disabled={held} onChange={(e) => setCopyCasebooks(e.target.checked)} className="mt-1" />
                <span>{ERASURE.copyCasebooks}</span>
              </label>
            </fieldset>
          ) : (
            <div data-testid="erasure-downloads" className="mt-6">
              <p className="pc-case-lightbox-question">{emailVerified === false ? ERASURE.copiesUnverified : ERASURE.copiesUnread}</p>
              <div className="mt-3 flex flex-wrap gap-3">
                <Pill ref={coachingRef} disabled={busy || held} onClick={() => download(onDownloadCoaching, coachingRef)}>
                  {ERASURE.downloadCoaching}
                </Pill>
                <Pill ref={accountRef} disabled={busy || held} onClick={() => download(onDownloadAccount, accountRef)}>
                  {ERASURE.downloadAccount}
                </Pill>
              </div>
              <p className="pc-case-lightbox-question">{ERASURE.downloadAccountHint}</p>
              {downloadFailed ? (
                <p role="alert" className="pc-guest-lightbox-text">
                  {ERASURE.downloadFailed}
                </p>
              ) : null}
            </div>
          )}

          {phase === "failed" || phase === "unsure" ? (
            <p role="alert" className="pc-guest-lightbox-text">
              {phase === "failed" ? ERASURE.failed : ERASURE.failedUnsure}
            </p>
          ) : null}
          {phase === "working" ? (
            <p role="status" className="pc-case-lightbox-question">
              {ERASURE.working}
            </p>
          ) : null}

          <div className="pc-case-lightbox-doors">
            <button ref={cancelRef} type="button" onClick={cancel} disabled={held} className={SAFE_BUTTON}>
              {ERASURE.cancel}
            </button>
            <Pill ref={confirmRef} onClick={confirm} disabled={busy || held}>
              {ERASURE.confirm}
            </Pill>
          </div>
        </div>
      </section>
    </div>,
    document.body,
  );
}

/** Which sentence the notice says about the confirmation email; null when the server's answer was lost. */
export function erasureEmailLine(email: ErasureRequestedFacts["email"]): string {
  if (email === null) return ERASURE.emailUnknown;
  if (!email.emailQueued) return ERASURE.emailNone;
  return email.copiesByEmail ? ERASURE.emailWithCopies : ERASURE.emailNoCopies;
}

/**
 * What the learner lands on after confirming, over the landing page once this
 * browser has logged out: the moment, whether a confirmation email is on its
 * way (never that one was sent), and how to change their mind. When the
 * server's answer was lost, only what the identity read could say: the moment
 * if it held one, else "30 days from today", and no word on the email either
 * way. A plain dialog with one door; Escape and the backdrop close it too.
 */
export function ErasureRequestedNotice({ requested, onClose }: { requested: ErasureRequestedFacts; onClose: () => void }): ReactNode {
  const paperRef = useRef<HTMLElement | null>(null);
  const doneRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    doneRef.current?.focus();
  }, []);
  useEscape(onClose);

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: DRAG_Z + 3, background: BACKDROP }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        ref={paperRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pc-erasure-requested-title"
        aria-describedby="pc-erasure-requested-body"
        tabIndex={-1}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.stopPropagation();
            onClose();
            return;
          }
          trapTab(event, paperRef.current);
        }}
        className="pc-case-lightbox pc-lightbox-in w-full max-w-[34rem] outline-none"
      >
        <div className="pc-case-lightbox-bar" style={{ cursor: "default" }}>
          <div className="pc-case-lightbox-kind">
            <CaseKindRow words={ERASURE.kind} glyph="plan" />
          </div>
        </div>
        <div className="pc-case-lightbox-body">
          <h2 id="pc-erasure-requested-title" className="pc-case-lightbox-title">
            {requested.eraseAfter === null ? ERASURE.requestedTitleUndated : ERASURE.requestedTitle(erasureMoment(requested.eraseAfter))}
          </h2>
          <div id="pc-erasure-requested-body">
            <p className="pc-guest-lightbox-text">{ERASURE.requestedSignedOut}</p>
            <p className="pc-guest-lightbox-text">{erasureEmailLine(requested.email)}</p>
            <p className="pc-guest-lightbox-text">{ERASURE.requestedKeep}</p>
          </div>
          <div className="pc-case-lightbox-doors">
            <button ref={doneRef} type="button" onClick={onClose} className={SAFE_BUTTON}>
              {ERASURE.done}
            </button>
          </div>
        </div>
      </section>
    </div>,
    document.body,
  );
}

type GracePhase = "idle" | "keeping" | "leaving" | "keepFailed" | "logOutFailed";

/**
 * THE GRACE PERIOD: what a signed-in account whose deletion is pending sees
 * instead of the app, the whole screen. "keep my account." cancels the request
 * (the root then asks who this is again and the app opens as before); "log
 * out." leaves it to run. Each door says so when it did not go through.
 */
export function ErasureGraceNotice({ eraseAfter, onKeep, onLogOut }: { eraseAfter: string; onKeep: () => Promise<boolean>; onLogOut: () => Promise<boolean> }): ReactNode {
  const [phase, setPhase] = useState<GracePhase>("idle");
  const pending = phase === "keeping" || phase === "leaving";
  const run = (start: "keeping" | "leaving", door: () => Promise<boolean>, failed: "keepFailed" | "logOutFailed"): void => {
    setPhase(start);
    void door().then((ok) => {
      // Done, the root draws the app in this notice's place; only a failure needs saying here.
      setPhase(ok ? "idle" : failed);
    });
  };

  return (
    <main data-testid="erasure-grace" className="flex min-h-svh items-center justify-center bg-paper px-gutter py-16">
      <div className="w-full max-w-[34rem]">
        <h1 className="m-0 font-display text-[24px] leading-tight font-bold">{ERASURE.graceTitle(erasureMoment(eraseAfter))}</h1>
        <p className="mt-4 mb-0 font-sans text-body">{ERASURE.graceBody}</p>
        {phase === "keepFailed" || phase === "logOutFailed" ? (
          <p role="alert" className="mt-4 mb-0 font-sans text-body">
            {phase === "keepFailed" ? ERASURE.keepFailed : ERASURE.logOutFailed}
          </p>
        ) : null}
        <div className="mt-8 flex flex-wrap gap-3">
          <Pill disabled={pending} onClick={() => run("keeping", onKeep, "keepFailed")}>
            {ERASURE.keep}
          </Pill>
          <Pill disabled={pending} onClick={() => run("leaving", onLogOut, "logOutFailed")}>
            {ERASURE.logOut}
          </Pill>
        </div>
      </div>
    </main>
  );
}
